<!-- src/components/OverviewMap.svelte
     首頁全景地圖 — 慢速自轉地球儀
     地圖資料從 straitScenes.js 讀,caption 文字從 JSON 讀
     桌機 / 手機共用一顆球,以容器最小邊為直徑自動適配 -->
<script>
  import { onMount, onDestroy } from 'svelte'
  import * as topojson from 'topojson'
  import * as d3 from 'd3'
  import { overviewScene } from '../data/straitScenes.js'
  import ContentDataStore from '../stores/ContentDataStore.js'

  // --- 可調參數 ---
  const ROTATE_SPEED_DEG_PER_SEC = 6   // 每秒旋轉幾度,360/6 = 60 秒繞地球一圈
  const INITIAL_LAMBDA = -60           // 初始視角的中心經度(負值,東半球先進入畫面)
  const INITIAL_PHI = -10              // 初始視角的中心緯度(略偏南,讓南北半球都看得到)

  let container, svg
  let timer                            // d3.timer 句柄,用來在 onDestroy 清掉

  $: overview = $ContentDataStore?.overview || {}

  onMount(async () => {
    const topo = await d3.json(overviewScene.landSource)
    const land = topojson.feature(topo, topo.objects.countries)
    const sphere = { type: 'Sphere' }

    // --- 投影:正射投影(orthographic) = 從太空看地球 ---
    const projection = d3.geoOrthographic()
      .rotate([INITIAL_LAMBDA, INITIAL_PHI])
      .clipAngle(90)                  // 自動裁掉地球背面的內容

    const path = d3.geoPath(projection)

    // --- 判斷某經緯度點是否在地球正面(用於 pin 的可見性) ---
    const visible = (coord) => {
      const [lambda, phi] = projection.rotate()
      const center = [-lambda, -phi]
      // d3.geoDistance 算大圓距離,弧度,< π/2 = 在正面半球
      return d3.geoDistance(coord, center) < Math.PI / 2
    }

    // --- 依容器大小決定球的尺寸 ---
    const sizeProjection = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      const radius = Math.min(w, h) / 2 * 0.85   // 留 15% 邊距
      projection
        .translate([w / 2, h / 2])
        .scale(radius)
      d3.select(svg).attr('viewBox', `0 0 ${w} ${h}`)
    }
    sizeProjection()

    // --- 建立各層 ---
    const root = d3.select(svg)

    // 海洋(球體本身,做為背景)
    const oceanPath = root.append('path')
      .datum(sphere)
      .attr('class', 'overview-ocean')

    // 經緯網
    const graticulePath = root.append('path')
      .datum(d3.geoGraticule10())
      .attr('class', 'graticule')

    // 陸地
    const landPath = root.append('path')
      .datum(land)
      .attr('class', 'overview-land')

    // 球體邊緣(描出地平線輪廓)
    const limbPath = root.append('path')
      .datum(sphere)
      .attr('class', 'overview-limb')

    // pin + 標籤層
    const pinsG = root.append('g').attr('class', 'overview-pins')

    const pinNodes = pinsG.selectAll('g.pin')
      .data(overviewScene.pins)
      .join('g')
      .attr('class', 'pin')

    pinNodes.append('circle')
      .attr('class', 'overview-pin')
      .attr('r', 6)

    pinNodes.append('text')
      .attr('class', 'overview-pin-label')
      .attr('dx', d => d.labelAnchor === 'left' ? -10 : 10)
      .attr('dy', 4)
      .attr('text-anchor', d => d.labelAnchor === 'left' ? 'end' : 'start')
      .text(d => d.name)

    // --- 每一幀重繪 ---
    const render = () => {
      oceanPath.attr('d', path)
      graticulePath.attr('d', path)
      landPath.attr('d', path)
      limbPath.attr('d', path)

      pinNodes.each(function(d) {
        const node = d3.select(this)
        const onFront = visible(d.coord)
        if (!onFront) {
          node.style('opacity', 0).style('pointer-events', 'none')
          return
        }
        const [x, y] = projection(d.coord)
        // 越靠近地平線(球體邊緣)淡出,讓正面中央的 pin 更突出
        const center = projection.rotate().map(v => -v)
        const dist = d3.geoDistance(d.coord, [center[0], center[1]])  // 0 ~ π/2
        const t = 1 - dist / (Math.PI / 2)                            // 0(邊緣) ~ 1(正中)
        const op = Math.max(0.15, Math.min(1, t * 1.4))
        node
          .style('opacity', op)
          .style('pointer-events', 'auto')
          .attr('transform', `translate(${x},${y})`)
      })
    }
    render()

    // --- 自轉 ---
    // 尊重使用者「減少動畫」的系統偏好(對前庭敏感的使用者友善)
    const prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (!prefersReducedMotion) {
      let lastTime = null
      timer = d3.timer((elapsed) => {
        if (lastTime === null) { lastTime = elapsed; return }
        const dt = (elapsed - lastTime) / 1000   // 秒
        lastTime = elapsed
        const [lambda, phi] = projection.rotate()
        projection.rotate([lambda + ROTATE_SPEED_DEG_PER_SEC * dt, phi])
        render()
      })
    }

    // --- 視窗 resize 時重新算尺寸 ---
    const onResize = () => { sizeProjection(); render() }
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  })

  onDestroy(() => {
    if (timer) timer.stop()
  })
</script>

<section class="overview-section">
  <div class="overview-map" bind:this={container}>
    <svg bind:this={svg}></svg>
  </div>
  {#if overview.caption_main || overview.caption_sub}
    <div class="overview-caption">
      {#if overview.caption_main}<span class="main">{overview.caption_main}</span>{/if}
      {#if overview.caption_sub}{overview.caption_sub}{/if}
    </div>
  {/if}
</section>

<style>
  .overview-section {
    display: flex;
    flex-direction: column;
    /* 桌面版維持滿版進場;手機版讓內容自然撐高,不再硬塞 100vh */
    min-height: 100vh;
  }
  .overview-map {
    flex: 1;
    width: 100%;
    min-height: 70vh;
    position: relative;
  }
  /* 手機:球容器改成「寬高比 1」,高度自動貼近球直徑,
     上下不再留多餘空白,caption 自然往上靠。
     section 撐滿視窗高並 justify-center,讓「球+caption」整體垂直置中 */
  @media (max-width: 640px) {
    .overview-section {
      min-height: 100vh;
      min-height: 100svh;        /* 扣掉手機 UI 列的穩定可視高 */
      justify-content: center;
    }
    .overview-map {
      flex: 0 0 auto;            /* 不再用 flex:1 撐高,讓 aspect-ratio 主導尺寸 */
      min-height: 0;
      aspect-ratio: 1 / 1;
      max-height: 90vh;          /* 極端視窗(橫過來、寬<640)時不超過視窗 */
    }
  }
  .overview-map svg {
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none;        /* 球體不接收任何指標事件,手機可正常滑頁 */
  }

  .overview-caption {
    text-align: center;
    padding: 0.5rem 1rem 1rem;
    color: #777777;
    letter-spacing: 0.1em;
    font-size: 0.9rem;
    font-family: 'Noto Sans TC', sans-serif;
  }
  /* 桌面版 caption 上面留多一點呼吸空間,維持原本節奏 */
  @media (min-width: 641px) {
    .overview-caption {
      padding-top: 1.5rem;
    }
  }
  .overview-caption .main {
    display: block;
    color: #d44545;
    font-size: 1.1rem;
    letter-spacing: 0.2em;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  /* 球體相關樣式 */
  :global(.overview-ocean) { fill: #ffffff; }
  :global(.graticule) { fill: none; stroke: #e8e4dc; stroke-width: 0.3px; }
  :global(.overview-land) { fill: #dcd7cd; stroke: #ffffff; stroke-width: 0.5px; }
  :global(.overview-limb) {
    fill: none;
    stroke: #c8c0b0;
    stroke-width: 1px;
  }
  :global(.overview-pin) {
    fill: #d44545;
    stroke: #ffffff;
    stroke-width: 1.5px;
    animation: pin-pulse 2.4s ease-in-out infinite;
  }
  @keyframes pin-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.4; }
  }
  @media (prefers-reduced-motion: reduce) {
    :global(.overview-pin) { animation: none; }
  }
  :global(.overview-pin-label) {
    fill: #333333;
    font-size: 13px;
    font-family: 'Noto Sans TC', sans-serif;
    font-weight: 500;
    pointer-events: none;
    paint-order: stroke;
    stroke: #f3f1ec;
    stroke-width: 3px;
    stroke-linejoin: round;
  }
</style>
